import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  toISO,
  fromISO,
  addDays,
  rangeDays,
  startOfWeek,
  DOW_SHORT,
  MONTH_SHORT,
} from "@/lib/dates";
import { Printer, ArrowLeft, CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function PrintWeekPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const startParam = params.get("start");
  const initialStart = useMemo(() => {
    if (startParam) {
      try {
        return fromISO(startParam);
      } catch {
        // fall through
      }
    }
    return startOfWeek(new Date(2026, 3, 6), 1); // Mon 6 Apr 26
  }, [startParam]);
  const [weekStart, setWeekStart] = useState(initialStart);
  const days = useMemo(() => rangeDays(startOfWeek(weekStart, 1), 7), [weekStart]);
  const startISO = toISO(days[0]);
  const endISO = toISO(days[days.length - 1]);

  const [routes, setRoutes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleAssignments, setVehicleAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, a, ab, v, va] = await Promise.all([
        api.listRoutes(),
        api.listStaff(),
        api.listAssignments({ start_date: startISO, end_date: endISO }),
        api.listAbsences({ start_date: startISO, end_date: endISO }),
        api.listVehicles(),
        api.listVehicleAssignments({ start_date: startISO, end_date: endISO }),
      ]);
      setRoutes(r);
      setStaff(s);
      setAssignments(a);
      setAbsences(ab);
      setVehicles(v);
      setVehicleAssignments(va);
    } catch {
      toast.error("Failed to load week");
    } finally {
      setLoading(false);
    }
  }, [startISO, endISO]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setParams({ start: toISO(days[0]) }, { replace: true });
  }, [days, setParams]);

  const staffById = useMemo(() => {
    const m = {};
    for (const s of staff) m[s.id] = s;
    return m;
  }, [staff]);
  const vehiclesById = useMemo(() => {
    const m = {};
    for (const v of vehicles) m[v.id] = v;
    return m;
  }, [vehicles]);
  const primaryVehicleByRoute = useMemo(() => {
    const m = {};
    for (const v of vehicles) if (v.route_id && !m[v.route_id]) m[v.route_id] = v;
    return m;
  }, [vehicles]);

  const overrideByRouteDate = useMemo(() => {
    const m = {};
    for (const va of vehicleAssignments) m[`${va.route_id}|${va.date}`] = va;
    return m;
  }, [vehicleAssignments]);

  const absenceByStaffDate = useMemo(() => {
    const m = {};
    for (const ab of absences) {
      if (!m[ab.staff_id]) m[ab.staff_id] = {};
      let cur = fromISO(ab.start_date);
      const end = fromISO(ab.end_date);
      while (cur <= end) {
        m[ab.staff_id][toISO(cur)] = ab.type;
        cur = addDays(cur, 1);
      }
    }
    return m;
  }, [absences]);

  const assignmentMap = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      if (!m[a.route_id]) m[a.route_id] = {};
      if (!m[a.route_id][a.date]) m[a.route_id][a.date] = {};
      m[a.route_id][a.date][a.slot] = a.staff_id;
    }
    return m;
  }, [assignments]);

  const shiftWeek = (n) => setWeekStart((d) => addDays(d, n * 7));

  const monthLabel = `${days[0].getDate()} ${MONTH_SHORT[days[0].getMonth()]} → ${days[6].getDate()} ${
    MONTH_SHORT[days[6].getMonth()]
  } ${days[6].getFullYear()}`;

  return (
    <div className="print-page min-h-screen bg-white">
      {/* Toolbar — hidden in print */}
      <div className="no-print sticky top-0 z-10 border-b-2 border-black bg-white px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/schedule")}
            data-testid="print-back"
            className="flex items-center gap-1.5 px-3 py-2 border border-black text-xs font-mono-plex tracking-widest hover:bg-zinc-100 active:scale-[0.98]"
          >
            <ArrowLeft size={12} weight="bold" />
            BACK
          </button>
          <div className="font-heading font-black text-lg tracking-tight leading-none">
            WEEKLY PRINT · {monthLabel}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-black">
            <button
              onClick={() => shiftWeek(-1)}
              data-testid="print-prev-week"
              className="px-2 h-9 border-r border-black hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <button
              onClick={() => setWeekStart(startOfWeek(new Date(2026, 3, 6), 1))}
              className="px-3 h-9 border-r border-black hover:bg-zinc-100 text-[10px] font-mono-plex tracking-widest font-semibold"
            >
              FIRST WEEK
            </button>
            <button
              onClick={() => shiftWeek(1)}
              data-testid="print-next-week"
              className="px-2 h-9 hover:bg-zinc-100 active:scale-[0.98]"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="print-btn"
            className="flex items-center gap-2 px-4 h-9 bg-black text-white text-xs font-mono-plex tracking-widest hover:bg-zinc-800 active:scale-[0.98]"
          >
            <Printer size={14} weight="bold" />
            PRINT
          </button>
        </div>
      </div>

      {/* Printable sheet */}
      <div className="max-w-[297mm] mx-auto p-6 print:p-3">
        <div className="border-b-2 border-black pb-3 mb-4 flex items-end justify-between">
          <div>
            <div className="font-heading font-black text-3xl tracking-tight leading-none">
              SM FORECAST · WEEKLY ROSTER
            </div>
            <div className="font-mono-plex text-xs text-zinc-700 mt-2 tracking-widest">
              WEEK OF {monthLabel.toUpperCase()}
            </div>
          </div>
          <div className="font-mono-plex text-[10px] text-zinc-500 tracking-widest text-right">
            {routes.length} ROUTES · {staff.length} STAFF · {vehicles.length} VEHICLES
            <div className="mt-1">GENERATED {new Date().toISOString().slice(0, 10)}</div>
          </div>
        </div>

        {loading ? (
          <div className="font-mono-plex text-xs text-zinc-500">LOADING…</div>
        ) : (
          <table className="w-full border-collapse text-[9px] font-mono-plex" data-testid="print-table">
            <colgroup>
              <col style={{ width: "42mm" }} />
              {days.map((d) => {
                const nonWork = [0, 1, 6].includes(d.getDay());
                return (
                  <col
                    key={toISO(d)}
                    style={{ width: nonWork ? "5%" : "12%" }}
                  />
                );
              })}
            </colgroup>
            <thead>
              <tr>
                <th className="border-2 border-black bg-black text-white text-left px-2 py-1.5 tracking-widest">
                  ROUTE / VEHICLE
                </th>
                {days.map((d) => {
                  const nonWork = [0, 1, 6].includes(d.getDay());
                  return (
                    <th
                      key={toISO(d)}
                      className={`border-2 border-black text-left px-2 py-1.5 tracking-widest ${
                        nonWork ? "bg-zinc-600 text-zinc-200" : "bg-black text-white"
                      }`}
                    >
                      <div className={nonWork ? "text-[8px]" : ""}>{DOW_SHORT[d.getDay()]}</div>
                      <div className={`font-bold ${nonWork ? "text-[9px]" : "text-[11px]"}`}>
                        {d.getDate()} {MONTH_SHORT[d.getMonth()]}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {routes.map((route) => {
                const primary = primaryVehicleByRoute[route.id];
                const slots = [
                  "driver",
                  ...Array.from({ length: route.loader_slots }, (_, i) => `loader-${i + 1}`),
                ];
                return (
                  <tr key={route.id}>
                    <td className="border border-black align-top px-2 py-1">
                      <div className="font-heading font-black text-sm leading-none">
                        {route.code}
                      </div>
                      <div className="text-[8px] tracking-widest text-zinc-600 mt-1">
                        {route.group} · D1 L{route.loader_slots}
                      </div>
                      {primary && (
                        <div className="mt-2 border border-black bg-zinc-100 px-1 py-0.5 text-[9px] tracking-tight font-semibold inline-block">
                          🚚 {primary.fleet_number}
                        </div>
                      )}
                    </td>
                    {days.map((d) => {
                      const iso = toISO(d);
                      const nonWork = [0, 1, 6].includes(d.getDay());
                      const ov = overrideByRouteDate[`${route.id}|${iso}`];
                      const displayVehicle = ov ? vehiclesById[ov.vehicle_id] : primary;
                      const dayAssigns = assignmentMap[route.id]?.[iso] || {};
                      return (
                        <td
                          key={iso}
                          className={`border border-black align-top px-1.5 py-1 ${
                            nonWork ? "bg-zinc-50 text-zinc-500" : ""
                          }`}
                          data-testid={`print-cell-${route.code}-${iso}`}
                        >
                          {displayVehicle && !nonWork && (
                            <div
                              className={`text-[9px] mb-1 pb-1 border-b border-dashed border-zinc-400 ${
                                ov ? "font-bold" : "text-zinc-600"
                              }`}
                            >
                              {ov ? "→ " : ""}
                              {displayVehicle.fleet_number}
                            </div>
                          )}
                          {slots.map((slot) => {
                            const sid = dayAssigns[slot];
                            const staffObj = sid ? staffById[sid] : null;
                            const absent = sid ? absenceByStaffDate[sid]?.[iso] : null;
                            const label =
                              slot === "driver" ? "D" : `L${slot.split("-")[1]}`;
                            if (nonWork) {
                              // Minimized rendering for Sat/Sun/Mon: only show if there's data
                              if (!staffObj) return null;
                              return (
                                <div
                                  key={slot}
                                  className="text-[7px] leading-tight truncate"
                                  title={staffObj.name + (absent ? ` (${absent})` : "")}
                                >
                                  <span className="text-zinc-400">{label}</span>{" "}
                                  <span className={absent ? "line-through" : ""}>
                                    {staffObj.name.split(" ").slice(-1)[0]}
                                  </span>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={slot}
                                className={`flex items-start gap-1 leading-tight ${
                                  slot === "driver" ? "font-semibold" : ""
                                }`}
                              >
                                <span className="text-zinc-500 shrink-0 w-3">{label}</span>
                                {staffObj ? (
                                  <span
                                    className={absent ? "line-through text-zinc-500" : ""}
                                    title={absent ? `on ${absent}` : ""}
                                  >
                                    {staffObj.name}
                                    {absent ? (
                                      <span className="ml-1 font-mono-plex text-[7px] tracking-widest text-rose-700">
                                        [{absent.slice(0, 3).toUpperCase()}]
                                      </span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="text-zinc-400">—</span>
                                )}
                              </div>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="mt-4 pt-3 border-t border-black flex items-center justify-between gap-4 flex-wrap text-[9px] font-mono-plex text-zinc-600 tracking-widest">
          <div className="flex items-center gap-3 flex-wrap">
            <span>D = DRIVER · L1/L2 = LOADERS</span>
            <span>→ FLEET = VEHICLE OVERRIDE (SERVICE DAY)</span>
            <span>[HOL] [SIC] [ABS] = STAFF ABSENCE</span>
          </div>
          <div>SM PLANNER · {monthLabel}</div>
        </div>
      </div>
    </div>
  );
}
